import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { PageHeader, Disclaimer } from "@/components/Common";
import { Plus, Search, MoreHorizontal, Eye, Pencil, ClipboardCheck, Send, CheckCircle2, RefreshCcw } from "lucide-react";
import { SUBMISSION_STATUSES, READY_STATUSES, RESULT_STATUSES } from "@/lib/constants";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";

// MP7 — Submissions list
// 14 columns: submission_id, request_id, requester_name, company, project_title,
// selected_track_id, selected_track_title, clearance_status_at_submission,
// date_prepared, prepared_by, document_packet_status, submission_status,
// result_status, deal_status, quick actions.

function subCls(s) {
  if (s === "Ready to Send") return "status-corporate";
  if (s === "Sent" || s === "Selected") return "status-client";
  if (s === "Draft") return "status-incomplete";
  if (["Internal Review", "Negotiating", "Shortlisted"].includes(s)) return "status-needs-review";
  return "status-incomplete";
}

const SORTABLE = {
  submission_id: (a, b) => (a.submission_id || "").localeCompare(b.submission_id || ""),
  date_prepared: (a, b) => (a.date_prepared || "").localeCompare(b.date_prepared || ""),
  project_title: (a, b) => (a.project_title || "").localeCompare(b.project_title || ""),
  submission_status: (a, b) => (a.submission_status || "").localeCompare(b.submission_status || ""),
  result_status: (a, b) => (a.result_status || "").localeCompare(b.result_status || ""),
};

export default function SubmissionsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("date_prepared");
  const [sortDir, setSortDir] = useState("desc");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.submission_status = status;
      const r = await api.get("/submissions", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { api.get("/tracks").then(r => setTracks(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const urlStatus = searchParams.get("status");
    if (urlStatus) setStatus(urlStatus);
  }, [searchParams]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const visible = useMemo(() => {
    let rows = items;
    if (q) {
      const needle = q.toLowerCase();
      rows = rows.filter(s => [
        s.submission_id, s.request_id, s.requester_name, s.company,
        s.project_title, s.selected_track_title, s.prepared_by,
      ].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    const cmp = SORTABLE[sortKey] || SORTABLE.date_prepared;
    rows = [...rows].sort((a, b) => sortDir === "asc" ? cmp(a, b) : cmp(b, a));
    return rows;
  }, [items, q, sortKey, sortDir]);

  function toggleSort(k) {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  }

  async function patchStatus(s, next) {
    // Compute the latest clearance snapshot is server-side; we just send a status update.
    try {
      await api.put(`/submissions/${s.id}`, { ...s, submission_status: next });
      toast.success(`Submission marked ${next}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function patchResult(s, next) {
    try {
      await api.put(`/submissions/${s.id}`, { ...s, result_status: next });
      toast.success(`Result updated to ${next}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  function eligibleForReadyToSend(s) {
    if (READY_STATUSES.includes(s.clearance_status_at_submission)) return true;
    const t = tracks.find(t => t.id === s.selected_track_id);
    return t ? READY_STATUSES.includes(t.clearance_status) : false;
  }

  return (
    <div data-testid="submissions-page">
      <PageHeader
        title="Submissions"
        subtitle="Internal draft packages for sync/license review and controlled external delivery."
        actions={writeAccess ? (
          <Button onClick={() => navigate("/submissions/new")} data-testid="submissions-new-button" style={{ background: "hsl(347 72% 22%)" }}>
            <Plus className="w-4 h-4 mr-2" />New Draft
          </Button>
        ) : (
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="submissions-readonly-badge">Read-only access</span>
        )}
      />

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[260px]">
            <label className="text-xs text-muted-foreground">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Submission ID, request ID, project, track, requester…"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9"
                data-testid="submissions-search-input"
              />
            </div>
          </div>
          <div className="min-w-[220px]">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="submissions-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {SUBMISSION_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <span className="text-xs text-muted-foreground self-end pb-2" data-testid="submissions-count">
            {visible.length} of {items.length}
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH onClick={() => toggleSort("submission_id")} active={sortKey === "submission_id"} dir={sortDir}>Submission</SortableTH>
                <TableHead>Request</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Company</TableHead>
                <SortableTH onClick={() => toggleSort("project_title")} active={sortKey === "project_title"} dir={sortDir}>Project</SortableTH>
                <TableHead>Track</TableHead>
                <TableHead>Snapshot clearance</TableHead>
                <SortableTH onClick={() => toggleSort("date_prepared")} active={sortKey === "date_prepared"} dir={sortDir}>Date prepared</SortableTH>
                <TableHead>Prepared by</TableHead>
                <TableHead>Packet</TableHead>
                <SortableTH onClick={() => toggleSort("submission_status")} active={sortKey === "submission_status"} dir={sortDir}>Status</SortableTH>
                <SortableTH onClick={() => toggleSort("result_status")} active={sortKey === "result_status"} dir={sortDir}>Result</SortableTH>
                <TableHead>Deal</TableHead>
                <TableHead className="w-[60px] sticky right-0 bg-card"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">Loading…</TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">No submissions match. Create a new draft to begin.</TableCell>
                </TableRow>
              )}
              {visible.map(s => {
                const eligible = eligibleForReadyToSend(s);
                return (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-secondary/40"
                    onClick={() => navigate(`/submissions/${s.id}`)}
                    data-testid={`submissions-row-${s.id}`}
                  >
                    <TableCell className="font-mono text-xs whitespace-nowrap">{s.submission_id}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">{s.request_id || "—"}</TableCell>
                    <TableCell className="max-w-[140px] truncate">{s.requester_name || "—"}</TableCell>
                    <TableCell className="max-w-[140px] truncate text-sm text-muted-foreground">{s.company || "—"}</TableCell>
                    <TableCell className="max-w-[180px] truncate font-medium">{s.project_title || "—"}</TableCell>
                    <TableCell className="max-w-[180px] truncate text-sm">{s.selected_track_title || (s.selected_track_id ? <span className="font-mono text-xs">{s.selected_track_id.slice(0, 8)}…</span> : "—")}</TableCell>
                    <TableCell className="text-xs">{s.clearance_status_at_submission || "—"}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(s.date_prepared || "").slice(0, 16).replace("T", " ") || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{s.prepared_by || "—"}</TableCell>
                    <TableCell className="text-xs">{s.document_packet_status || "—"}</TableCell>
                    <TableCell><span className={`inline-flex px-2 py-1 rounded text-xs whitespace-nowrap ${subCls(s.submission_status)}`}>{s.submission_status}</span></TableCell>
                    <TableCell className="text-xs">{s.result_status || "—"}</TableCell>
                    <TableCell className="text-xs">{s.deal_status || "—"}</TableCell>
                    <TableCell className="sticky right-0 bg-card" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`submissions-actions-${s.id}`} aria-label="Submission actions">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[240px]">
                          <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => navigate(`/submissions/${s.id}`)} data-testid={`submissions-action-view-${s.id}`}>
                            <Eye className="w-4 h-4 mr-2" />View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/submissions/${s.id}`)} data-testid={`submissions-action-edit-${s.id}`}>
                            <Pencil className="w-4 h-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/submissions/${s.id}?tab=package`)} data-testid={`submissions-action-review-${s.id}`}>
                            <ClipboardCheck className="w-4 h-4 mr-2" />Review Package
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={!eligible || s.submission_status === "Ready to Send"}
                            onClick={() => patchStatus(s, "Ready to Send")}
                            data-testid={`submissions-action-ready-${s.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />Mark Ready to Send
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={s.submission_status === "Sent"}
                            onClick={() => patchStatus(s, "Sent")}
                            data-testid={`submissions-action-sent-${s.id}`}
                          >
                            <Send className="w-4 h-4 mr-2" />Mark Sent
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-muted-foreground">Update result</DropdownMenuLabel>
                          {RESULT_STATUSES.map(r => (
                            <DropdownMenuItem key={r} onClick={() => patchResult(s, r)} data-testid={`submissions-action-result-${r.replace(/\s+/g, "-").toLowerCase()}-${s.id}`}>
                              <RefreshCcw className="w-4 h-4 mr-2" />{r}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Submission packages are internal workflow drafts · Compliance support only · Not legal advice · Human review required before external delivery.
      </Disclaimer>
    </div>
  );
}

function SortableTH({ children, onClick, active, dir }) {
  return (
    <TableHead onClick={onClick} className="cursor-pointer select-none">
      <span className="inline-flex items-center gap-1">
        {children}
        {active && <span className="text-[10px] text-muted-foreground">{dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </TableHead>
  );
}
